import React, { useEffect, useState } from "react";
import { t } from "ttag";

import { CountdownTimerBlock as Value } from "@reactivated";

import { FlipDigit } from "./FlipDigit";
import { ShrimpAnimation } from "./ShrimpAnimation";

import styles from "./index.module.scss";

interface Props {
    value: Value;
}

interface TimeRemaining {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
}

/**
 * Parse an ISO datetime string (with offset) and return the
 * difference from now broken into days/hours/minutes/seconds.
 */
const getTimeRemaining = (targetDate: string): TimeRemaining => {
    const diff = Math.max(0, new Date(targetDate).getTime() - Date.now());

    return {
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
    };
};

const padZero = (n: number): string => String(n).padStart(2, "0");

export const CountdownTimerBlock = (props: Props) => {
    const targetDate = props.value.target_date;

    const [time, setTime] = useState<TimeRemaining>(() =>
        targetDate
            ? getTimeRemaining(targetDate)
            : { days: 0, hours: 0, minutes: 0, seconds: 0 },
    );

    useEffect(() => {
        if (!targetDate) return;
        const interval = setInterval(() => {
            setTime(getTimeRemaining(targetDate));
        }, 1000);
        return () => clearInterval(interval);
    }, [targetDate]);

    return (
        <section
            className={styles.root}
            role="timer"
            aria-label={t`Countdown timer`}
        >
            <div className={styles.content}>
                <div className={styles.timer}>
                    {(
                        [
                            [time.days, t`Days`],
                            [time.hours, t`Hours`],
                            [time.minutes, t`Minutes`],
                            [time.seconds, t`Seconds`],
                        ] as [number, string][]
                    ).map(([value, label], idx) => {
                        const [tens, ones] = padZero(value).split("");
                        return (
                            <React.Fragment key={label}>
                                <div className={styles.segment}>
                                    <span className={styles.number}>
                                        <FlipDigit value={tens} />
                                        <FlipDigit value={ones} />
                                    </span>
                                    <span className={styles.label}>
                                        {label}
                                    </span>
                                </div>
                            </React.Fragment>
                        );
                    })}
                </div>
                <ShrimpAnimation seconds={time.seconds} />
            </div>
        </section>
    );
};
